import {
  formatarDuracao,
  type FiltroRelatorio,
  type LinhaAtendimento,
  RELATORIOS,
  ROTULO_TIPO_ATENDIMENTO,
  type PainelRelatorios,
  type TipoRelatorio,
} from '@mymaxx/shared';
import { Aviso, Botao } from '@mymaxx/ui';
import { useQuery } from '@tanstack/react-query';
import { Download, FileDown, FileText, Search, Star, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ErroApi } from '../lib/api';
import { gestaoApi } from '../lib/gestao-api';
import { relatoriosApi } from '../lib/relatorios-api';
import { Barras, Colunas, formatarDia, TabelaFiltravel } from '../relatorios/Graficos';

type Aba = 'indicadores' | 'atendimentos' | 'baixar';

/**
 * O que foi clicado nos gráficos — o filtro cruzado, à moda de BI.
 *
 * Separado do filtro do formulário de propósito: ali em cima se escolhe o recorte grande
 * (período, setor, busca), aqui se fatia dentro dele com o mouse. Cada dimensão é uma
 * lista porque Ctrl+clique acumula.
 */
interface Selecao {
  setorIds: string[];
  atendenteIds: string[];
  motivoTagIds: string[];
  dias: string[];
  horas: number[];
}

const SEM_SELECAO: Selecao = {
  setorIds: [],
  atendenteIds: [],
  motivoTagIds: [],
  dias: [],
  horas: [],
};

/** Clique simples troca a seleção; com Ctrl/Cmd acumula; clicar no que já estava tira. */
function alternar<T>(atual: T[], valor: T, acumular: boolean): T[] {
  if (!acumular) return atual.length === 1 && atual[0] === valor ? [] : [valor];
  return atual.includes(valor) ? atual.filter((v) => v !== valor) : [...atual, valor];
}

/** Data de hoje e de N dias atrás em AAAA-MM-DD, para os atalhos de período. */
function diasAtras(dias: number): string {
  return new Date(Date.now() - dias * 86_400_000).toISOString().slice(0, 10);
}
const HOJE = () => new Date().toISOString().slice(0, 10);

const ROTULO_STATUS: Record<string, string> = {
  bot: 'Automático',
  fila: 'Na fila',
  em_atendimento: 'Em atendimento',
  aguardando_cliente: 'Aguardando cliente',
  encerrada: 'Encerrado',
};

/** Relatórios: os números do período e a consulta de atendimentos com exportação. */
export function Relatorios() {
  const [aba, setAba] = useState<Aba>('indicadores');
  const [filtro, setFiltro] = useState<Partial<FiltroRelatorio>>({
    de: diasAtras(30),
    ate: HOJE(),
  });
  const [busca, setBusca] = useState('');
  // Só consulta depois que a pessoa para de digitar — senão é uma ida ao servidor por tecla.
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [cursor, setCursor] = useState<string | undefined>();
  const [erro, setErro] = useState<string | null>(null);
  const [baixando, setBaixando] = useState<string | null>(null);
  const [selecao, setSelecao] = useState<Selecao>(SEM_SELECAO);

  const setores = useQuery({ queryKey: ['setores'], queryFn: gestaoApi.listarSetores });
  const usuarios = useQuery({ queryKey: ['usuarios'], queryFn: () => gestaoApi.listarUsuarios() });
  const motivos = useQuery({ queryKey: ['motivos'], queryFn: gestaoApi.listarMotivos });

  useEffect(() => {
    const espera = setTimeout(() => setBuscaAplicada(busca.trim()), 400);
    return () => clearTimeout(espera);
  }, [busca]);

  // O que vai para o servidor: o formulário mais o que foi clicado nos gráficos. Dimensão
  // sem nada selecionado sai de fora da URL — lista vazia ali significaria "nada passa".
  const consultado: Partial<FiltroRelatorio> = {
    ...filtro,
    busca: buscaAplicada || undefined,
    setorIds: selecao.setorIds.length ? selecao.setorIds : undefined,
    atendenteIds: selecao.atendenteIds.length ? selecao.atendenteIds : undefined,
    motivoTagIds: selecao.motivoTagIds.length ? selecao.motivoTagIds : undefined,
    dias: selecao.dias.length ? selecao.dias : undefined,
    horas: selecao.horas.length ? selecao.horas : undefined,
  };
  const temSelecao = Object.values(selecao).some((v) => v.length > 0);

  const painel = useQuery({
    queryKey: ['relatorio-painel', consultado],
    queryFn: () => relatoriosApi.painel(consultado),
    enabled: aba === 'indicadores',
  });
  const lista = useQuery({
    queryKey: ['relatorio-atendimentos', consultado, cursor],
    queryFn: () => relatoriosApi.atendimentos({ ...consultado, cursor, limite: 50 }),
    enabled: aba === 'atendimentos',
  });

  function alterar(campo: keyof FiltroRelatorio, valor: string) {
    setCursor(undefined);
    setFiltro((atual) => ({ ...atual, [campo]: valor || undefined }));
  }

  /** Um clique num gráfico ou numa linha de tabela. */
  function selecionar<C extends keyof Selecao>(
    dimensao: C,
    valor: Selecao[C][number],
    acumular: boolean,
  ) {
    setCursor(undefined);
    setSelecao((atual) => ({
      ...atual,
      [dimensao]: alternar(atual[dimensao] as (typeof valor)[], valor, acumular),
    }));
  }

  async function baixar(tipo: TipoRelatorio, formato: 'xlsx' | 'csv') {
    setBaixando(`${tipo}-${formato}`);
    setErro(null);
    try {
      await relatoriosApi.exportar(tipo, consultado, formato);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui gerar o arquivo');
    } finally {
      setBaixando(null);
    }
  }

  async function baixarTranscricao(linha: LinhaAtendimento) {
    setErro(null);
    try {
      await relatoriosApi.transcricao(linha.id, linha.protocolo);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui gerar o arquivo');
    }
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Relatórios</h1>
          <p className="text-sm text-neutral-500">
            Os números da operação e a consulta de qualquer atendimento.
          </p>
        </div>
        <Botao variante="secundario" onClick={() => setAba('baixar')}>
          <FileDown size={15} /> Baixar relatórios
        </Botao>
      </header>

      {/* Filtros: valem para as duas abas e para a exportação. */}
      <section className="grid gap-3 rounded-card border border-neutral-200 p-3 sm:grid-cols-2 lg:grid-cols-4 dark:border-neutral-800">
        <Campo rotulo="De">
          <input
            type="date"
            value={filtro.de ?? ''}
            onChange={(e) => alterar('de', e.target.value)}
            className={ENTRADA}
          />
        </Campo>
        <Campo rotulo="Até">
          <input
            type="date"
            value={filtro.ate ?? ''}
            onChange={(e) => alterar('ate', e.target.value)}
            className={ENTRADA}
          />
        </Campo>
        <Campo rotulo="Setor">
          <select
            value={filtro.setorId ?? ''}
            onChange={(e) => alterar('setorId', e.target.value)}
            className={ENTRADA}
          >
            <option value="">Todos</option>
            {setores.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nome}
              </option>
            ))}
          </select>
        </Campo>
        <Campo rotulo="Atendente">
          <select
            value={filtro.atendenteId ?? ''}
            onChange={(e) => alterar('atendenteId', e.target.value)}
            className={ENTRADA}
          >
            <option value="">Todos</option>
            {usuarios.data?.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome}
              </option>
            ))}
          </select>
        </Campo>
        <Campo rotulo="Motivo">
          <select
            value={filtro.motivoTagId ?? ''}
            onChange={(e) => alterar('motivoTagId', e.target.value)}
            className={ENTRADA}
          >
            <option value="">Todos</option>
            {motivos.data?.flatMap((c) =>
              c.motivos.map((m) => (
                <option key={m.id} value={m.id}>
                  {c.nome} · {m.nome}
                </option>
              )),
            )}
          </select>
        </Campo>
        <Campo rotulo="Avaliação">
          <select
            value={String(filtro.avaliacao ?? '')}
            onChange={(e) => alterar('avaliacao', e.target.value)}
            className={ENTRADA}
          >
            <option value="">Todas</option>
            {[5, 4, 3, 2, 1].map((n) => (
              <option key={n} value={n}>
                {n} estrela{n > 1 ? 's' : ''}
              </option>
            ))}
            <option value="sem">Sem avaliação</option>
          </select>
        </Campo>
        <Campo rotulo="Situação">
          <select
            value={filtro.status ?? ''}
            onChange={(e) => alterar('status', e.target.value)}
            className={ENTRADA}
          >
            <option value="">Todas</option>
            {Object.entries(ROTULO_STATUS).map(([valor, rotulo]) => (
              <option key={valor} value={valor}>
                {rotulo}
              </option>
            ))}
          </select>
        </Campo>
        <Campo rotulo="Protocolo, CPF/CNPJ ou nome">
          <div className="relative">
            <Search
              size={14}
              className="absolute top-1/2 left-2 -translate-y-1/2 text-neutral-400"
            />
            <input
              value={busca}
              placeholder="Ex.: 2026.09.23/08.26.37-000003"
              onChange={(e) => {
                setCursor(undefined);
                setBusca(e.target.value);
              }}
              className={`${ENTRADA} pl-7`}
            />
          </div>
        </Campo>
      </section>

      <div className="flex flex-wrap gap-2 text-xs">
        <span className="text-neutral-500">Período rápido:</span>
        {[
          ['Hoje', 0],
          ['7 dias', 7],
          ['30 dias', 30],
          ['90 dias', 90],
        ].map(([rotulo, dias]) => (
          <button
            key={rotulo}
            type="button"
            onClick={() => {
              setCursor(undefined);
              setFiltro((a) => ({ ...a, de: diasAtras(Number(dias)), ate: HOJE() }));
            }}
            className="rounded-full border border-neutral-300 px-2 py-0.5 hover:bg-neutral-100 dark:border-neutral-700 dark:hover:bg-neutral-800"
          >
            {rotulo}
          </button>
        ))}
      </div>

      {temSelecao && (
        <div className="flex flex-wrap items-center gap-2 rounded-card border border-marca-300 bg-marca-50 px-3 py-2 text-xs dark:border-marca-800 dark:bg-marca-950/40">
          <span className="font-medium">Filtrando por:</span>
          {resumoDaSelecao(selecao, {
            setores: setores.data,
            usuarios: usuarios.data,
            motivos: painel.data?.base?.porMotivo ?? painel.data?.porMotivo,
          }).map((texto) => (
            <span key={texto} className="rounded-full bg-white px-2 py-0.5 dark:bg-neutral-900">
              {texto}
            </span>
          ))}
          <button
            type="button"
            onClick={() => {
              setSelecao(SEM_SELECAO);
              setCursor(undefined);
            }}
            className="ml-auto inline-flex items-center gap-1 underline"
          >
            <X size={12} /> Limpar seleção
          </button>
        </div>
      )}

      {erro && <Aviso tipo="erro">{erro}</Aviso>}

      <div className="flex gap-1 border-b border-neutral-200 text-sm dark:border-neutral-800">
        {(
          [
            ['indicadores', 'Indicadores'],
            ['atendimentos', 'Atendimentos'],
            ['baixar', 'Baixar'],
          ] as const
        ).map(([valor, rotulo]) => (
          <button
            key={valor}
            type="button"
            onClick={() => setAba(valor)}
            className={`px-3 py-2 transition ${
              aba === valor
                ? 'border-b-2 border-marca-600 font-medium text-marca-700 dark:text-marca-300'
                : 'text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'
            }`}
          >
            {rotulo}
          </button>
        ))}
      </div>

      {aba === 'indicadores' &&
        (painel.isPending ? (
          <p className="text-sm text-neutral-500">Calculando…</p>
        ) : painel.data ? (
          <Indicadores dados={painel.data} selecao={selecao} aoSelecionar={selecionar} />
        ) : null)}

      {aba === 'atendimentos' && (
        <TabelaAtendimentos
          itens={lista.data?.itens ?? []}
          total={lista.data?.total ?? 0}
          carregando={lista.isPending}
          temMais={!!lista.data?.proximoCursor}
          onMais={() => setCursor(lista.data?.proximoCursor ?? undefined)}
          onTranscricao={(linha) => void baixarTranscricao(linha)}
        />
      )}

      {aba === 'baixar' && <Downloads baixando={baixando} onBaixar={baixar} />}
    </div>
  );
}

/**
 * Os relatórios prontos, um cartão cada.
 *
 * Todos saem com os filtros que estão na tela, inclusive a seleção dos gráficos — daí o
 * aviso no alto: é fácil baixar "as pausas" achando que é o mês e levar só o dia que
 * ficou selecionado num clique anterior.
 */
function Downloads({
  baixando,
  onBaixar,
}: {
  baixando: string | null;
  onBaixar: (tipo: TipoRelatorio, formato: 'xlsx' | 'csv') => Promise<void>;
}) {
  const grupos = [...new Set(RELATORIOS.map((r) => r.grupo))];
  return (
    <div className="space-y-5">
      <p className="text-xs text-neutral-500">
        Cada arquivo sai com o período e os filtros escolhidos aqui em cima.
      </p>
      {grupos.map((grupo) => (
        <section key={grupo} className="space-y-2">
          <h2 className="text-sm font-medium">{grupo}</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {RELATORIOS.filter((r) => r.grupo === grupo).map((r) => (
              <article
                key={r.id}
                className="flex flex-col rounded-card border border-neutral-200 p-3 dark:border-neutral-800"
              >
                <h3 className="text-sm font-medium">{r.nome}</h3>
                <p className="mt-1 flex-1 text-xs text-neutral-500">{r.descricao}</p>
                <div className="mt-3 flex gap-2">
                  <Botao
                    variante="secundario"
                    carregando={baixando === `${r.id}-xlsx`}
                    onClick={() => void onBaixar(r.id, 'xlsx')}
                  >
                    <FileDown size={14} /> Excel
                  </Botao>
                  <Botao
                    variante="secundario"
                    carregando={baixando === `${r.id}-csv`}
                    onClick={() => void onBaixar(r.id, 'csv')}
                  >
                    <Download size={14} /> CSV
                  </Botao>
                </div>
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/** O que está selecionado, por extenso — o que aparece na faixa acima das abas. */
function resumoDaSelecao(
  selecao: Selecao,
  catalogos: {
    setores?: { id: string; nome: string }[];
    usuarios?: { id: string; nome: string }[];
    motivos?: { motivoTagId: string | null; motivo: string }[];
  },
): string[] {
  const nome = <T extends { nome?: string; motivo?: string }>(
    lista: T[] | undefined,
    id: string,
    chave: (item: T) => string,
  ) => lista?.find((i) => chave(i) === id);

  const textos: string[] = [];
  for (const id of selecao.setorIds) {
    textos.push(nome(catalogos.setores, id, (s) => s.id)?.nome ?? 'Setor');
  }
  for (const id of selecao.atendenteIds) {
    textos.push(nome(catalogos.usuarios, id, (u) => u.id)?.nome ?? 'Atendente');
  }
  for (const id of selecao.motivoTagIds) {
    const achado = catalogos.motivos?.find((m) => (m.motivoTagId ?? 'sem') === id);
    textos.push(achado?.motivo ?? 'Motivo');
  }
  for (const dia of selecao.dias) textos.push(formatarDia(dia));
  for (const hora of selecao.horas) textos.push(`${String(hora).padStart(2, '0')}h`);
  return textos;
}

function Indicadores({
  dados,
  selecao,
  aoSelecionar,
}: {
  dados: PainelRelatorios;
  selecao: Selecao;
  aoSelecionar: <C extends keyof Selecao>(
    dimensao: C,
    valor: Selecao[C][number],
    acumular: boolean,
  ) => void;
}) {
  const i = dados.indicadores;
  // Durante um deploy a tela nova pode conversar alguns minutos com a API velha, que não
  // manda `base`. Sem isto, o painel inteiro fica preto no meio da janela de subida.
  const base = dados.base ?? dados;

  // Cada gráfico desenha o total do período por trás (vindo de `base`) e o que sobrou da
  // seleção na frente. É o que deixa o gráfico clicado inteiro na tela, para haver onde
  // clicar de volta, e mostra de olho o tamanho da fatia dentro do todo.
  const valoresPorDia = new Map(dados.porDia.map((d) => [d.dia, d.total]));
  const colunasDia = base.porDia.map((d) => ({
    chave: d.dia,
    rotulo: formatarDia(d.dia),
    valor: valoresPorDia.get(d.dia) ?? 0,
    total: d.total,
    descricao: `${formatarDia(d.dia)}: ${valoresPorDia.get(d.dia) ?? 0} de ${d.total}`,
  }));

  const valoresPorHora = new Map(dados.porHora.map((h) => [h.hora, h.total]));
  const colunasHora = base.porHora.map((h) => {
    const rotulo = `${String(h.hora).padStart(2, '0')}h`;
    return {
      chave: String(h.hora),
      rotulo,
      valor: valoresPorHora.get(h.hora) ?? 0,
      total: h.total,
      descricao: `${rotulo}: ${valoresPorHora.get(h.hora) ?? 0} de ${h.total}`,
    };
  });

  const valoresPorMotivo = new Map(dados.porMotivo.map((m) => [m.motivoTagId ?? 'sem', m.total]));
  const fatiasMotivo = base.porMotivo.map((m) => {
    const chave = m.motivoTagId ?? 'sem';
    const valor = valoresPorMotivo.get(chave) ?? 0;
    return {
      chave,
      rotulo: m.categoria ? `${m.categoria} · ${m.motivo}` : m.motivo,
      valor,
      total: m.total,
      detalhe: `${valor} (${m.percentual}%)`,
    };
  });

  const totalPorSetor = new Map(dados.porSetor.map((s) => [s.setorId, s.total]));
  const totalPorAtendente = new Map(dados.porAtendente.map((a) => [a.atendenteId, a.atendidos]));

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Cartao
          titulo="Atendimentos"
          valor={String(i.totalAtendimentos)}
          rodape={`${i.emAberto} em aberto`}
        />
        <Cartao
          titulo="Espera média"
          valor={formatarDuracao(i.tmeSeg)}
          rodape="da fila até o atendente"
        />
        <Cartao
          titulo="Duração média"
          valor={formatarDuracao(i.tmaSeg)}
          rodape={`1ª resposta em ${formatarDuracao(i.primeiraRespostaSeg)}`}
        />
        <Cartao
          titulo="Satisfação"
          valor={i.csat ? `${i.csat.toFixed(2)} / 5` : '—'}
          rodape={
            <>
              {/* A nota em porcentagem do máximo: "4,6 de 5" é a régua do atendimento,
                  "92%" é a régua com que a diretoria compara setores e meses. */}
              {i.csatPercentual !== null && (
                <span className="block font-medium text-amber-600 dark:text-amber-400">
                  {i.csatPercentual?.toFixed(2)}% do máximo
                </span>
              )}
              <span className="block">{i.totalAvaliacoes} avaliação(ões)</span>
            </>
          }
          icone={<Star size={14} className="text-amber-500" />}
        />
        <Cartao titulo="Encerrados" valor={String(i.encerrados)} rodape="no período" />
        <Cartao
          titulo="Abandono na fila"
          valor={String(i.abandonados)}
          rodape="cliente saiu antes de ser atendido"
        />
        <Cartao
          titulo="Resolvidos pelo bot"
          valor={String(i.resolvidosPeloBot)}
          rodape="sem passar por atendente"
        />
        <Cartao
          titulo="Motivo mais comum"
          valor={dados.porMotivo[0]?.motivo ?? '—'}
          rodape={dados.porMotivo[0] ? `${dados.porMotivo[0].percentual}% do total` : ''}
          pequeno
        />
      </div>

      <p className="text-xs text-neutral-500">
        Clique em qualquer barra ou linha para filtrar o painel inteiro. Ctrl (ou Cmd) soma
        seleções; clicar de novo desfaz.
      </p>

      <div className="grid gap-4 xl:grid-cols-2">
        <Painel titulo="Atendimentos por dia">
          <Colunas
            dados={colunasDia}
            selecao={selecao.dias}
            aoSelecionar={(chave, acumular) => aoSelecionar('dias', chave, acumular)}
          />
        </Painel>
        <Painel titulo="Por hora do dia">
          <Colunas
            dados={colunasHora}
            cor="teal"
            selecao={selecao.horas.map(String)}
            aoSelecionar={(chave, acumular) => aoSelecionar('horas', Number(chave), acumular)}
          />
        </Painel>
        <Painel titulo="Por motivo">
          <Barras
            itens={fatiasMotivo}
            selecao={selecao.motivoTagIds}
            aoSelecionar={(chave, acumular) => aoSelecionar('motivoTagIds', chave, acumular)}
          />
        </Painel>
        <Painel titulo="Por setor">
          <TabelaFiltravel
            itens={base.porSetor}
            chave={(s) => s.setorId}
            selecao={selecao.setorIds}
            aoSelecionar={(chave, acumular) => aoSelecionar('setorIds', chave, acumular)}
            vazio="Nenhum setor no período."
            colunas={[
              { titulo: 'Setor', conteudo: (s) => s.setor },
              {
                titulo: 'Total',
                alinhar: 'direita',
                conteudo: (s) => fatiaDoTotal(totalPorSetor.get(s.setorId), s.total),
              },
              { titulo: 'Espera', alinhar: 'direita', conteudo: (s) => formatarDuracao(s.tmeSeg) },
              { titulo: 'Duração', alinhar: 'direita', conteudo: (s) => formatarDuracao(s.tmaSeg) },
              { titulo: 'CSAT', alinhar: 'direita', conteudo: (s) => s.csat?.toFixed(2) ?? '—' },
            ]}
          />
        </Painel>
      </div>

      <Painel titulo="Por atendente">
        <TabelaFiltravel
          itens={base.porAtendente}
          chave={(a) => a.atendenteId}
          selecao={selecao.atendenteIds}
          aoSelecionar={(chave, acumular) => aoSelecionar('atendenteIds', chave, acumular)}
          vazio="Ninguém atendeu no período."
          colunas={[
            { titulo: 'Atendente', conteudo: (a) => a.atendente },
            {
              titulo: 'Atendidos',
              alinhar: 'direita',
              conteudo: (a) => fatiaDoTotal(totalPorAtendente.get(a.atendenteId), a.atendidos),
            },
            { titulo: 'Encerrados', alinhar: 'direita', conteudo: (a) => a.encerrados },
            {
              titulo: 'Duração média',
              alinhar: 'direita',
              conteudo: (a) => formatarDuracao(a.tmaSeg),
            },
            {
              titulo: '1ª resposta',
              alinhar: 'direita',
              conteudo: (a) => formatarDuracao(a.primeiraRespostaSeg),
            },
            { titulo: 'CSAT', alinhar: 'direita', conteudo: (a) => a.csat?.toFixed(2) ?? '—' },
          ]}
        />
      </Painel>
    </div>
  );
}

/**
 * "3 de 27" enquanto há seleção, "27" quando não há.
 *
 * Sem isto a tabela mostraria o número do período inteiro mesmo com uma fatia escolhida, e
 * ninguém entenderia por que os cartões lá em cima dizem outra coisa.
 */
function fatiaDoTotal(daSelecao: number | undefined, doPeriodo: number): string {
  const valor = daSelecao ?? 0;
  return valor === doPeriodo ? String(doPeriodo) : `${valor} de ${doPeriodo}`;
}

function TabelaAtendimentos({
  itens,
  total,
  carregando,
  temMais,
  onMais,
  onTranscricao,
}: {
  itens: LinhaAtendimento[];
  total: number;
  carregando: boolean;
  temMais: boolean;
  onMais: () => void;
  onTranscricao: (linha: LinhaAtendimento) => void;
}) {
  if (carregando) return <p className="text-sm text-neutral-500">Buscando…</p>;

  return (
    <div className="space-y-3">
      <p className="text-xs text-neutral-500">
        {total === 0 ? 'Nenhum atendimento com esses filtros.' : `${total} atendimento(s)`}
      </p>

      <div className="overflow-x-auto rounded-card border border-neutral-200 dark:border-neutral-800">
        <table className="w-full text-xs">
          <thead className="bg-neutral-50 text-left text-neutral-500 dark:bg-neutral-900">
            <tr>
              <th className="px-3 py-2">Protocolo</th>
              <th className="px-3 py-2">Cliente</th>
              <th className="px-3 py-2">Setor</th>
              <th className="px-3 py-2">Atendente</th>
              <th className="px-3 py-2">Motivo</th>
              <th className="px-3 py-2">Tipo</th>
              <th className="px-3 py-2">Início</th>
              <th className="px-3 py-2 text-right">Espera</th>
              <th className="px-3 py-2 text-right">Duração</th>
              <th className="px-3 py-2 text-center">Nota</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {itens.map((l) => (
              <tr key={l.id} className="border-t border-neutral-100 dark:border-neutral-800">
                <td className="px-3 py-2 font-mono whitespace-nowrap">{l.protocolo}</td>
                <td className="px-3 py-2">{l.cliente ?? '—'}</td>
                <td className="px-3 py-2">{l.setor ?? '—'}</td>
                <td className="px-3 py-2">{l.atendente ?? '—'}</td>
                <td className="px-3 py-2">{l.motivo ?? '—'}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {ROTULO_TIPO_ATENDIMENTO[l.tipoAtendimento]}
                  {l.recontatos > 0 && (
                    <span
                      className="ml-1 rounded bg-amber-100 px-1 text-[10px] text-amber-900 dark:bg-amber-950 dark:text-amber-200"
                      title={`${l.recontatos} atendimento(s) nos 30 dias anteriores${
                        l.protocoloAnterior ? ` · anterior: ${l.protocoloAnterior}` : ''
                      }`}
                    >
                      recontato
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {new Date(l.iniciadaEm).toLocaleString('pt-BR')}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {formatarDuracao(l.esperaSeg)}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {formatarDuracao(l.atendimentoSeg)}
                </td>
                <td className="px-3 py-2 text-center">{l.avaliacao ?? '—'}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => onTranscricao(l)}
                    title="Baixar a transcrição em PDF"
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-marca-700 hover:bg-marca-50 dark:text-marca-300 dark:hover:bg-marca-950"
                  >
                    <FileText size={13} /> PDF
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {temMais && (
        <Botao variante="secundario" onClick={onMais}>
          Carregar mais
        </Botao>
      )}
    </div>
  );
}

const ENTRADA =
  'w-full rounded-lg border border-neutral-300 px-2 py-1.5 text-sm dark:border-neutral-700 dark:bg-neutral-900';

function Campo({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-300">
      {rotulo}
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Cartao({
  titulo,
  valor,
  rodape,
  icone,
  pequeno,
}: {
  titulo: string;
  valor: string;
  /** Uma ou duas linhas abaixo do número; aceita marcação para destacar parte dela. */
  rodape?: React.ReactNode;
  icone?: React.ReactNode;
  pequeno?: boolean;
}) {
  return (
    <div className="rounded-card border border-neutral-200 p-3 dark:border-neutral-800">
      <p className="flex items-center gap-1 text-xs text-neutral-500">
        {icone} {titulo}
      </p>
      <p className={`mt-1 font-semibold ${pequeno ? 'text-sm' : 'text-2xl'}`}>{valor}</p>
      {rodape && <div className="text-[11px] text-neutral-500">{rodape}</div>}
    </div>
  );
}

function Painel({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="rounded-card border border-neutral-200 p-3 dark:border-neutral-800">
      <h2 className="mb-2 text-sm font-medium">{titulo}</h2>
      {children}
    </section>
  );
}
